'use client';

import { useTheme } from '@mui/material/styles';
import {
  type PickersShortcutsItemContext,
  type PickersShortcutsItem as MuiPickersShortcutsItem,
  type PickersShortcutsProps,
} from '@mui/x-date-pickers-pro';

import { ListItem, type ListItemProps } from '../../list-item/ListItem';
import { List, type ListProps } from '../../list/List';
import {
  SelectionChip,
  type SelectionChipProps,
} from '../../selection-chip/SelectionChip';

export type SelectionChipStartIcon = SelectionChipProps['startIcon'];

export type PickersShortcutsItem<TValue> = MuiPickersShortcutsItem<TValue> & {
  startIcon?: SelectionChipStartIcon;
  id?: string;
};

export type ShortcutsProps<TValue> = Omit<ListProps, 'onChange'> & {
  items?: PickersShortcutsItem<TValue>[];
  isValid?: PickersShortcutsProps<TValue>['isValid'];
  changeImportance?: PickersShortcutsProps<TValue>['changeImportance'];
  onChange?: PickersShortcutsProps<TValue>['onChange'];
  isLandscape?: PickersShortcutsProps<TValue>['isLandscape'];
  startIcon?: SelectionChipStartIcon;
  listItemProps?: ListItemProps;
  value?: TValue;
};

export const Shortcuts = <TValue,>(props: ShortcutsProps<TValue>) => {
  const {
    items,
    isValid,
    changeImportance = 'set', // This is needed for MUI's internal `Shortcuts` `onChange` that it will pass
    onChange,
    isLandscape: _isLandscape, // MUI will pass this down to the `Shortcuts` component so we need to destructure it away
    startIcon: globalStartIcon,
    listItemProps,
    value,
    ...otherProps
  } = props;
  const theme = useTheme();

  if (!items || items.length === 0 || !isValid || !onChange) {
    return null;
  }

  const handleChange = (
    newValue: TValue,
    importance: ShortcutsProps<TValue>['changeImportance'],
    item: PickersShortcutsItemContext
  ) => {
    onChange?.(newValue, importance ?? 'set', item);
  };

  const resolvedItems = items.map(({ getValue, startIcon, ...item }, index) => {
    const newValue = getValue({ isValid });
    const itemId = item.id ?? `shortcut-${index}-${item.label}`;
    const newValueKey = JSON.stringify(newValue);
    const isSelected = JSON.stringify(value) === newValueKey;

    return {
      id: itemId,
      label: item.label,
      onClick: () => {
        handleChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
      startIcon: startIcon ?? globalStartIcon,
      isSelected,
    };
  });

  const {
    sx: listPropsSx,
    gap: listPropsGap,
    ...otherListProps
  } = otherProps ?? {};

  return (
    <List
      gap={listPropsGap}
      sx={[
        {
          display: 'flex',
          gap: theme.spacing(1),
          padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
          flexShrink: 0,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        ...(listPropsSx && Array.isArray(listPropsSx)
          ? listPropsSx
          : [listPropsSx]),
      ]}
      {...otherListProps}
    >
      {resolvedItems.map((item) => {
        return (
          <ListItem
            key={item.id}
            disableGutters
            disablePadding
            sx={{ width: 'fit-content' }}
            {...listItemProps}
          >
            <SelectionChip
              variant="toggle"
              label={item.label}
              onClick={item.onClick}
              disabled={item.disabled}
              selected={item.isSelected}
              startIcon={item.startIcon}
            />
          </ListItem>
        );
      })}
    </List>
  );
};
